/*
 * Copyright (c) Kalaora 2022.
 * Author: fharold
 *
 * hilo-oauth-manager
 * Videoservice.ts
 */

import KareviAPIService from "../tools/service/KareviAPIService";
import Video from "../models/Video";
import QueryFilter from "../tools/model/pagination/Filters";
import {Listener} from "../tools/service/Service";
import Config from "../config";
import {Selectable} from "../tools/model/form/Selectable";
import {PaginationFilters} from "../tools/pagination/PaginationFilters";
import Category from "../models/Category";
import {VideoFileType} from "../component/page/VideoPage";
import {ProgressWatcher} from "../tools/service/KareviHTTPService";
import CatalogService from "./CatalogService";

export interface XMLHttpRequestProgressEvent extends ProgressEvent {
    lengthComputable: boolean;
    loaded: number;
    path: string[];
    position: number;
    total: number;
    totalSize: number;
}

export default class VideoService extends KareviAPIService<Video, Listener<Video>> {
    public static SINGLE_ROUTE = '/category/{categoryId}/video/{videoId}';
    public static GET_FILE_ROUTE = '/category/{categoryId}/video/{videoId}/{fileType}';
    public static POST_FILE_ROUTE = '/category/{categoryId}/video/{videoId}/{fileType}';
    public static LIST_ROUTE = '/category/{categoryId}/videos';
    public static LIST_ALL_ROUTE = '/videos';
    public static id = "VideoService";

    public static convertToSelectable(videos: Array<Video>): Array<Selectable<Video>> {
        let result: Array<Selectable<Video>> = [];

        for (let video of videos) {
            result.push({
                value: video,
                name: video.title,
                disabled: false
            })
        }

        return result;
    }

    /**
     * start
     */
    public start() {
        super.start();
    }

    /**
     * stop
     */
    public stop() {
        super.stop();
    }

    /**
     * createVideo
     * @return {Promise<Video>}
     * @param category
     * @param dto
     */
    public async createVideo(category: Category, dto: Map<string, any> | FormData): Promise<Video> {
        const endpoint = this.httpService.buildRoute(VideoService.LIST_ROUTE, {
            categoryId: category.id
        })


        return await this.post(endpoint, dto);
    }

    public getFileURL(fileType: VideoFileType, video: Video): string {
        const endpoint = this.httpService.buildRoute(VideoService.GET_FILE_ROUTE, {
            videoId: video.id,
            categoryId: video.categoryId,
            fileType: fileType.toString()
        })

        return Config.KAREVI_API_URL + endpoint
    }

    /**
     * updateVideo
     * @return {Promise<Video>}
     * @param dto
     * @param video
     */
    public async updateVideo(video: Video, dto: Map<string, any> | FormData): Promise<Video> {
        const route = this.httpService.buildRoute(VideoService.SINGLE_ROUTE, {
            videoId: video.id,
            categoryId: video.categoryId
        });

        return await this.put(route, dto);
    }

    /**
     * assignVideoToCatalog
     * @return {Promise<Video>}
     * @param videoId
     * @param catalogId
     */
    public async assignVideoToCatalog(videoId: string, catalogId: string): Promise<Video> {
        console.log("building url, catalogId is :", catalogId)
        const route = this.httpService.buildRoute(CatalogService.VIDEO_IN_CATALOG, {
            videoId: videoId,
            catalogId: catalogId
        });

        return await this.post(route, {});
    }

    /**
     * removeVideoFromCatalog
     * @return {Promise<Video>}
     * @param videoId
     * @param catalogId
     */
    public async removeVideoFromCatalog(videoId: string, catalogId: string): Promise<Boolean> {
        const route = this.httpService.buildRoute(CatalogService.VIDEO_IN_CATALOG, {
            videoId: videoId,
            catalogId: catalogId
        });

        return await this.delete(route);
    }

    /**
     * uploadFile
     * @return {Promise<Video>}
     * @param video
     * @param fileType
     * @param payload
     * @param progressWatcher
     */
    public async uploadFile(video: Video, fileType: VideoFileType, payload: Map<string, any> | FormData, progressWatcher?: ProgressWatcher): Promise<Video> {
        const route = this.httpService.buildRoute(VideoService.POST_FILE_ROUTE, {
            videoId: video.id,
            categoryId: video.categoryId,
            fileType: fileType.toString().toUpperCase()
        });

        return await this.postFile(route, payload, progressWatcher);
    }

    /**
     * findById
     * @returns {Promise<void>}
     */
    public async findById(category: Category, id: string): Promise<Video> {
        const route = this.httpService.buildRoute(VideoService.SINGLE_ROUTE, {
            videoId: id,
            categoryId: category.id
        });

        return await this.getOne<Video>(route);
    }

    /**
     * findAll
     * @returns {Promise<Video[]>}
     */
    public async findAll(): Promise<Video[]> {
        const videoFilters: QueryFilter<PaginationFilters> = new QueryFilter<PaginationFilters>({
            page: 0,
            size: 10000,
        });

        return await this.getList<Video, PaginationFilters>(VideoService.LIST_ROUTE, videoFilters);
    }

    /**
     * findAll
     * @returns {Promise<Video[]>}
     */
    public async findAllInEveryCategory(): Promise<Video[]> {
        const videoFilters: QueryFilter<PaginationFilters> = new QueryFilter<PaginationFilters>({
            page: 0,
            size: 10000,
        });

        return await this.getList<Video, PaginationFilters>(VideoService.LIST_ALL_ROUTE, videoFilters);
    }

    /**
     * removeVideo
     * @param video
     * @returns {Promise<void>}
     */
    public async removeVideo(video: Video) : Promise<Boolean> {
        const route = this.httpService.buildRoute(VideoService.SINGLE_ROUTE, {
            videoId: video.id,
            categoryId: video.categoryId
        });

        return await this.delete<Video>(route);

    }
}
